import { useCallback, useEffect, useState } from 'react';
import { Button } from '@tervior-tech/tervior-shared-ui-react';
import { useAuth } from '../onboarding/AuthContext';
import { DASHBOARD_SESSION_KEY_NAME } from '../../app/DashboardSessionKeyContext';
import {
  ApiProblemError,
  deleteApiKeyPermanently,
  listApiKeys,
  reactivateApiKey,
  revokeApiKey,
  type ApiKeyMetadata,
  type CreatedApiKey,
} from '../../lib/api-client';
import { Card } from '../../components/ui/Card';
import { Badge } from '../../components/ui/Badge';
import { Alert } from '../../components/ui/Alert';
import { LoadingState } from '../../components/ui/Spinner';
import { ConfirmDialog } from '../../components/ui/ConfirmDialog';
import { CreateKeyDialog } from './CreateKeyDialog';

function extractProblemMessage(err: unknown, fallback: string): string {
  return err instanceof ApiProblemError ? (err.problem.detail ?? err.problem.title) : fallback;
}

/**
 * `/api-keys` (TTP-244, R8) — lists the org's own API keys, lets the user
 * create/revoke them. Uses the Firebase ID token (`/internal/api-keys`),
 * NOT the dashboard session key — same auth shape as onboarding's
 * `ApiKeysContext.tsx`.
 *
 * Filters out `DASHBOARD_SESSION_KEY_NAME` from the listing — that key is
 * Trivio's own internal plumbing (see `DashboardSessionKeyContext.tsx`),
 * never something the lojista created; showing it here (or letting them
 * revoke it by hand) would confuse them and, on next reload, this app would
 * just silently re-mint a replacement anyway.
 */
export function ApiKeysPage() {
  const { user } = useAuth();
  const [idToken, setIdToken] = useState<string | null>(null);
  const [keys, setKeys] = useState<ApiKeyMetadata[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [revokeTarget, setRevokeTarget] = useState<ApiKeyMetadata | null>(null);
  const [revoking, setRevoking] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<ApiKeyMetadata | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [reactivatingId, setReactivatingId] = useState<string | null>(null);

  const load = useCallback(() => {
    if (!user) {
      return;
    }
    setLoading(true);
    setError(null);
    user
      .getIdToken()
      .then((token) => {
        setIdToken(token);
        return listApiKeys(token);
      })
      .then((list) => {
        setKeys(list.filter((k) => k.name !== DASHBOARD_SESSION_KEY_NAME));
      })
      .catch((err: unknown) => {
        setError(extractProblemMessage(err, 'Não foi possível carregar as chaves.'));
      })
      .finally(() => setLoading(false));
  }, [user]);

  useEffect(() => {
    load();
  }, [load]);

  async function handleConfirmRevoke() {
    if (!idToken || !revokeTarget) return;
    setRevoking(true);
    setActionError(null);
    try {
      await revokeApiKey(idToken, revokeTarget.id);
      const revokedAt = new Date().toISOString();
      setKeys((prev) => prev.map((k) => (k.id === revokeTarget.id ? { ...k, revoked_at: revokedAt } : k)));
      setRevokeTarget(null);
    } catch (err) {
      setActionError(extractProblemMessage(err, 'Não foi possível revogar a chave agora.'));
    } finally {
      setRevoking(false);
    }
  }

  /**
   * TTP-266: undoes an accidental/erroneous revoke (e.g. the onboarding-loop
   * incident's revoked keys) without minting a brand-new key — the hash/
   * prefix stay the same, so anything already configured with the old
   * secret keeps working. No confirmation dialog (reactivating isn't
   * destructive), unlike revoke/delete below.
   */
  async function handleReactivate(key: ApiKeyMetadata) {
    if (!idToken) return;
    setReactivatingId(key.id);
    setActionError(null);
    try {
      const updated = await reactivateApiKey(idToken, key.id);
      setKeys((prev) => prev.map((k) => (k.id === key.id ? updated : k)));
    } catch (err) {
      setActionError(extractProblemMessage(err, 'Não foi possível reativar a chave agora.'));
    } finally {
      setReactivatingId(null);
    }
  }

  /** TTP-266: irreversible hard delete — only ever reachable for an already-revoked key (the button below isn't even rendered for an active one; the backend also enforces this with a 409). */
  async function handleConfirmDelete() {
    if (!idToken || !deleteTarget) return;
    setDeleting(true);
    setActionError(null);
    try {
      await deleteApiKeyPermanently(idToken, deleteTarget.id);
      setKeys((prev) => prev.filter((k) => k.id !== deleteTarget.id));
      setDeleteTarget(null);
    } catch (err) {
      setActionError(extractProblemMessage(err, 'Não foi possível excluir a chave agora.'));
    } finally {
      setDeleting(false);
    }
  }

  function handleCreated(created: CreatedApiKey) {
    setKeys((prev) => [
      {
        id: created.id,
        prefix: created.prefix,
        mode: created.mode,
        name: created.name,
        created_at: created.created_at,
        last_used_at: created.last_used_at,
        revoked_at: created.revoked_at,
      },
      ...prev,
    ]);
  }

  return (
    <div className="flex flex-col gap-6">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div className="flex flex-col gap-2">
          <h1 className="text-2xl font-medium text-foreground">API keys</h1>
          <p className="text-sm text-muted-foreground">Chaves usadas para autenticar chamadas à API pública do Trivio.</p>
        </div>
        <Button type="button" variant="default" onClick={() => setCreateOpen(true)}>
          Criar chave
        </Button>
      </div>

      {actionError ? (
        <Alert variant="error" title="Ação não concluída">
          {actionError}
        </Alert>
      ) : null}
      {error ? (
        <Alert variant="error" title="Não foi possível carregar as chaves">
          {error}
        </Alert>
      ) : null}

      {loading ? (
        <LoadingState label="Carregando chaves…" />
      ) : keys.length === 0 ? (
        <Card className="p-6">
          <p className="text-sm text-foreground">Nenhuma chave criada ainda.</p>
        </Card>
      ) : (
        <div className="overflow-x-auto border border-border">
          <table className="w-full border-collapse text-left text-sm">
            <thead>
              <tr className="border-b border-border text-xs font-medium uppercase tracking-widest text-muted-foreground">
                <th className="px-4 py-3">Prefixo</th>
                <th className="px-4 py-3">Nome</th>
                <th className="px-4 py-3">Modo</th>
                <th className="px-4 py-3">Criada em</th>
                <th className="px-4 py-3">Último uso</th>
                <th className="px-4 py-3">Status</th>
                <th className="px-4 py-3">Ações</th>
              </tr>
            </thead>
            <tbody>
              {keys.map((k) => (
                <tr key={k.id} className="border-b border-border/60 last:border-b-0">
                  <td className="px-4 py-3 font-mono text-xs">{k.prefix}…</td>
                  <td className="px-4 py-3">{k.name}</td>
                  <td className="px-4 py-3">
                    <Badge variant={k.mode === 'live' ? 'accent' : 'neutral'}>
                      {k.mode === 'live' ? 'Produção' : 'Sandbox'}
                    </Badge>
                  </td>
                  <td className="px-4 py-3 font-mono text-xs text-muted-foreground">
                    {new Date(k.created_at).toLocaleString('pt-BR')}
                  </td>
                  <td className="px-4 py-3 font-mono text-xs text-muted-foreground">
                    {k.last_used_at ? new Date(k.last_used_at).toLocaleString('pt-BR') : 'Nunca usada'}
                  </td>
                  <td className="px-4 py-3">
                    {k.revoked_at ? <Badge variant="neutral">Revogada</Badge> : <Badge variant="success">Ativa</Badge>}
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex gap-2">
                      {!k.revoked_at ? (
                        <Button type="button" variant="outline" size="sm" onClick={() => setRevokeTarget(k)}>
                          Revogar
                        </Button>
                      ) : (
                        <>
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            onClick={() => handleReactivate(k)}
                            disabled={reactivatingId === k.id}
                          >
                            {reactivatingId === k.id ? 'Reativando…' : 'Reativar'}
                          </Button>
                          <Button type="button" variant="outline" size="sm" onClick={() => setDeleteTarget(k)}>
                            Excluir
                          </Button>
                        </>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <CreateKeyDialog open={createOpen} idToken={idToken} onClose={() => setCreateOpen(false)} onCreated={handleCreated} />

      <ConfirmDialog
        open={revokeTarget !== null}
        title="Revogar chave"
        description={
          revokeTarget
            ? `Tem certeza que deseja revogar a chave "${revokeTarget.name}" (${revokeTarget.prefix}…)? A revogação pode levar até 60 segundos para valer em todas as chamadas.`
            : ''
        }
        confirmLabel="Revogar"
        cancelLabel="Voltar"
        loading={revoking}
        onConfirm={handleConfirmRevoke}
        onCancel={() => setRevokeTarget(null)}
      />

      <ConfirmDialog
        open={deleteTarget !== null}
        title="Excluir chave permanentemente"
        description={
          deleteTarget
            ? `Tem certeza que deseja excluir permanentemente a chave "${deleteTarget.name}" (${deleteTarget.prefix}…)? Essa ação não pode ser desfeita.`
            : ''
        }
        confirmLabel="Excluir"
        cancelLabel="Voltar"
        loading={deleting}
        onConfirm={handleConfirmDelete}
        onCancel={() => setDeleteTarget(null)}
      />
    </div>
  );
}
